import Tutorial, { type TutorialStep } from './Tutorial'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStorage } from '@/contexts/StorageContext'
import { useTutorial } from '@/hooks/useTutorial'
import { fetchWithAuth } from '@/lib/api/client'
import { useThemeMode } from '@/contexts/ThemeContext'

// Get API URL from environment
const API_URL = import.meta.env.VITE_API_URL!

declare global {
  interface Window {
    desktopContext?: {
      openWindow: (
        title: string,
        contentType: 'auth' | 'fileExplorer' | 'settings' | 'chat' | 'profile' | 'representation',
        size?: { width: number | string; height: number | string },
        initialPosition?: { x: number; y: number },
        extraData?: object
      ) => number | undefined
      closeAllWindows: () => void
    }
  }
}

// Pre-defined theme for tutorial using specific color palette
const HONCHO_TUTORIAL_THEME = 
{
  "light": {
    "colors": {
      "primaryBg": "#FFFFFF",
      "secondaryBg": "#F9FAFB",
      "terminalBg": "#F0F2F6",
      "headerBg": "#FFFFFF",
      "primaryBorder": "#E5E7EB",
      "secondaryBorder": "#D1D5DB",
      "headerBorder": "#E5E7EB",
      "terminalBorder": "#D1D5DB",
      "primaryText": "#111827",
      "secondaryText": "#374151",
      "tertiaryText": "#6B7280",
      "darkText": "#000000",
      "lightText": "#FFFFFF",
      "textUser": "#111827",
      "headerText": "#111827",
      "secondaryHeaderText": "#111827",
      "visualAccent1": "#B7DBFF",
      "visualAccent2": "#6B7280",
      "visualAccent3": "#374151",
      "visualAccent4": "#9CA3AF",
      "primaryAccent": "#B7DBFF",
      "hoverAccent": "#9EC4F8",
      "successBg": "rgba(183,219,255,0.15)",
      "successBorder": "#B7DBFF",
      "errorBg": "#FEF2F2",
      "errorBorder": "#FECACA",
      "assistantBg": "#FFFFFF",
      "systemMsgBg": "#F9FAFB",
      "userBg": "#FFFFFF",
      "userMsgBg": "#F3F4F6",
      "assistantMsgBg": "rgba(183,219,255,0.18)",
      "assistantMsgGlow": "rgba(183,219,255,0.3)",
      "assistantMsgAccent": "#B7DBFF",
      "userMsgBgAlpha": "rgba(183,219,255,0.15)",
      "userMsgGlow": "rgba(183,219,255,0.25)",
      "userMsgAccent": "#60A5FA",
      "scrollbarThumb": "#9CA3AF",
      "scrollbarTrack": "#F3F4F6",
      "gridOverlay": "rgba(0,0,0,0.02)",
      "white": "#FFFFFF",
      "gridLine": "rgba(0,0,0,0.05)",
      "windowBg": "#FFFFFF",
      "windowBgAlpha": "rgba(255,255,255,0.95)",
      "windowBorder": "#E5E7EB",
      "windowBorderActive": "#B7DBFF",
      "windowHeaderBg": "#F9FAFB",
      "windowHeaderBorder": "#E5E7EB",
      "windowControlBg": "#FFFFFF",
      "windowControlBgHover": "#F3F4F6",
      "navBg": "#FFFFFF",
      "navBorder": "#E5E7EB",
      "navTabBgActive": "#B7DBFF",
      "navTabBgHover": "#E6F0FF",
      "mobileNavBg": "#FFFFFF",
      "mobileNavBorder": "#E5E7EB",
      "mobileNavIconHover": "#E6F0FF",
      "mobileNavIconActive": "#B7DBFF",
      "folderSelectedBg": "#B7DBFF",
      "folderSelectedText": "#000000",
      "emptyStateText": "#6B7280",
      "webIndicatorOnBg": "rgba(183,219,255,0.15)",
      "webIndicatorOnBorder": "rgba(183,219,255,0.3)",
      "webIndicatorOffBg": "rgba(0,0,0,0.05)",
      "webIndicatorOffBorder": "rgba(0,0,0,0.1)",
      "orbListening": "rgba(183,219,255,0.5)",
      "orbSpeaking": "#B7DBFF",
      "orbProcessing": "#111827",
      "orbListeningGlow": "rgba(183,219,255,0.8)",
      "orbSpeakingGlow": "rgba(183,219,255,1)",
      "orbProcessingGlow": "rgba(17,24,39,0.5)",
      "orbDefaultGlow": "rgba(0,0,0,0.1)"
    }
  },
  "dark": {
    "colors": {
      "primaryBg": "#000000",
      "secondaryBg": "#111827",
      "terminalBg": "#1F2937",
      "headerBg": "#111827",
      "primaryBorder": "#374151",
      "secondaryBorder": "#4B5563",
      "headerBorder": "#374151",
      "terminalBorder": "#4B5563",
      "primaryText": "#F9FAFB",
      "secondaryText": "#D1D5DB",
      "tertiaryText": "#9CA3AF",
      "darkText": "#000000",
      "lightText": "#FFFFFF",
      "textUser": "#F9FAFB",
      "headerText": "#B7DBFF",
      "secondaryHeaderText": "#FFFFFF",
      "visualAccent1": "#B7DBFF",
      "visualAccent2": "#D1D5DB",
      "visualAccent3": "#FFFFFF",
      "visualAccent4": "#9CA3AF",
      "primaryAccent": "#B7DBFF",
      "hoverAccent": "#B7DBFF",
      "successBg": "rgba(183,219,255,0.1)",
      "successBorder": "#B7DBFF",
      "errorBg": "#7F1D1D",
      "errorBorder": "#F87171",
      "assistantBg": "#111827",
      "systemMsgBg": "#1F2937",
      "userBg": "#111827",
      "userMsgBg": "#1F2937",
      "assistantMsgBg": "rgba(183,219,255,0.15)",
      "assistantMsgGlow": "rgba(183,219,255,0.4)",
      "assistantMsgAccent": "#B7DBFF",
      "userMsgBgAlpha": "rgba(183,219,255,0.18)",
      "userMsgGlow": "rgba(183,219,255,0.3)",
      "userMsgAccent": "#B7DBFF",
      "scrollbarThumb": "#475569",
      "scrollbarTrack": "#111827",
      "gridOverlay": "rgba(255,255,255,0.02)",
      "white": "#FFFFFF",
      "gridLine": "rgba(255,255,255,0.05)",
      "windowBg": "#111827",
      "windowBgAlpha": "rgba(17,24,39,0.95)",
      "windowBorder": "#374151",
      "windowBorderActive": "#B7DBFF",
      "windowHeaderBg": "#1F2937",
      "windowHeaderBorder": "#374151",
      "windowControlBg": "#374151",
      "windowControlBgHover": "#4B5563",
      "navBg": "#111827",
      "navBorder": "#374151",
      "navTabBgActive": "#B7DBFF",
      "navTabBgHover": "#B7DBFF",
      "mobileNavBg": "#111827",
      "mobileNavBorder": "#374151",
      "mobileNavIconHover": "#B7DBFF",
      "mobileNavIconActive": "#B7DBFF",
      "folderSelectedBg": "#B7DBFF",
      "folderSelectedText": "#000000",
      "emptyStateText": "#9CA3AF",
      "webIndicatorOnBg": "rgba(183,219,255,0.2)",
      "webIndicatorOnBorder": "rgba(183,219,255,0.4)",
      "webIndicatorOffBg": "rgba(255,255,255,0.05)",
      "webIndicatorOffBorder": "rgba(255,255,255,0.15)",
      "orbListening": "rgba(183,219,255,0.5)",
      "orbSpeaking": "#B7DBFF",
      "orbProcessing": "#FFFFFF",
      "orbListeningGlow": "rgba(183,219,255,0.8)",
      "orbSpeakingGlow": "rgba(183,219,255,1)",
      "orbProcessingGlow": "rgba(255,255,255,0.5)",
      "orbDefaultGlow": "rgba(255,255,255,0.1)"
    }
  }
}

export function useDesktopTutorial() {
  const { t } = useTranslation()
  const tutorial = useTutorial('desktop-walkthrough')
  const themeCommandSubmittedRef = useRef(false)
  const { themes, fetchThemes } = useStorage()
  const marketplaceTypedRef = useRef(false)
  const { colors } = useThemeMode()

  // Check if theme exists ONLY ONCE when the hook is first called
  // This prevents the steps array from changing mid-tutorial when the theme is created
  const includeThemeStepsRef = useRef<boolean | null>(null)
  const previousIsOpenRef = useRef(tutorial.isOpen)

  // Reset the theme steps check when tutorial is restarted
  useEffect(() => {
    const wasOpen = previousIsOpenRef.current
    const isNowOpen = tutorial.isOpen

    // If tutorial just opened (transition from false to true), re-check theme existence
    if (!wasOpen && isNowOpen) {
      console.log('[Tutorial] Tutorial opened/restarted, re-checking theme existence')
      const alreadyExists = (themes || []).some(
        (t: any) =>
          typeof t?.name === 'string' &&
          (t.name.toLowerCase() === 'honcho_theme.json' ||
            t.name.toLowerCase() === 'honcho_theme')
      )
      includeThemeStepsRef.current = !alreadyExists
    }

    previousIsOpenRef.current = isNowOpen
  }, [tutorial.isOpen, themes])

  // Initial check on first mount
  if (includeThemeStepsRef.current === null) {
    const alreadyExists = (themes || []).some(
      (t: any) =>
        typeof t?.name === 'string' &&
        (t.name.toLowerCase() === 'honcho_theme.json' ||
          t.name.toLowerCase() === 'honcho_theme')
    )
    includeThemeStepsRef.current = !alreadyExists
  }

  // Helper function to create theme directly via API (mocking tool call)
  const createTutorialTheme = async () => {
    console.log('[Tutorial] createTutorialTheme called')
    try {
      console.log('[Tutorial] API_URL:', API_URL)
      console.log('[Tutorial] Theme data:', HONCHO_TUTORIAL_THEME)

      const response = await fetchWithAuth(`${API_URL}/api/themes/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: 'honcho_theme.json',
          content: JSON.stringify(HONCHO_TUTORIAL_THEME, null, 2),
          description: 'A clean theme with mint and blue accents',
          language: 'json',
        }),
      })

      console.log('[Tutorial] Response status:', response.status)

      if (!response.ok) {
        const errorData = await response.json()
        console.error('[Tutorial] API error:', errorData)
        throw new Error(errorData.error || 'Failed to create theme')
      }

      // Dispatch events to refresh the file explorer/storage
      console.log('[Tutorial] Dispatching refresh events')
      window.dispatchEvent(new Event('working-representation-refresh'))
      window.dispatchEvent(new Event('storage-refresh'))
      window.dispatchEvent(new CustomEvent('theme-created', { detail: { name: 'honcho_theme.json' } }))

      const result = await response.json()
      console.log('[Tutorial] Theme created successfully:', result)

      // Manually refresh themes list
      console.log('[Tutorial] Refreshing themes list...')
      await fetchThemes()
      console.log('[Tutorial] Themes list refreshed')

      return result
    } catch (error) {
      console.error('[Tutorial] Error creating tutorial theme:', error)
      throw error
    }
  }

  const themeAlreadyExists = () => {
    try {
    } catch {}
    try {
      return (themes || []).some(
        (t: any) =>
          typeof t?.name === 'string' &&
          (t.name.toLowerCase() === 'honcho_theme.json' ||
            t.name.toLowerCase() === 'honcho_theme')
      )
    } catch {
      return false
    }
  }

  const ensureChatOpen = () => {
    try {
      window.desktopContext?.openWindow('Chat', 'chat', { width: 1200, height: 800 })
      // Blur any focused input so arrow keys work for window navigation during tutorial
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement
        if (activeElement && (activeElement.tagName === 'TEXTAREA' || activeElement.tagName === 'INPUT')) {
          console.log('[Tutorial] Blurring focused input to enable arrow keys')
          activeElement.blur()
        }
      }, 300)
    } catch {}
  }

  const ensureFilesOpen = () => {
    try {
      window.desktopContext?.openWindow('Files', 'fileExplorer', { width: 900, height: 600 })
    } catch {}
  }

  // Build steps dynamically so we can skip theme-creation flow if it already exists
  // Use the stable ref value that was determined when the hook first mounted
  const includeThemeSteps = includeThemeStepsRef.current

  const baseSteps: TutorialStep[] = [
    {
      target: '.desktop',
      title: t('tutorial.welcome.title'),
      description: t('tutorial.welcome.description'),
      placement: 'center',
      action: () => {
        // Close all windows to start with a clean slate
        try {
          window.desktopContext?.closeAllWindows()
        } catch {}
      },
    },
    {
      target: '[data-nav-type="chat"]',
      title: t('tutorial.openChat.title'),
      description: t('tutorial.openChat.description'),
      placement: 'bottom',
      action: () => {
        const el = document.querySelector('[data-nav-type="chat"]')
        el?.classList.add('tutorial-pulse')
      },
    },
    {
      target: '[data-content-type="chat"]',
      title: t('tutorial.windows.title'),
      description: t('tutorial.windows.description'),
      placement: 'center',
      action: async () => {
        ensureChatOpen()
        // Wait for the window to fully render and position itself
        // The Tutorial component's 150ms timeout isn't enough for the window animation
        await new Promise(resolve => setTimeout(resolve, 500))
        // Trigger Tutorial to reposition now that window is fully rendered
        window.dispatchEvent(new Event('resize'))
      },
    },
    {
      target: '[data-chat-input-area]',
      title: t('tutorial.messageInput.title'),
      description: t('tutorial.messageInput.description'),
      placement: 'top',
      offsetY: -12,
      action: ensureChatOpen,
    },
    {
      target: '[data-chat-input-root], [data-slash-dropdown]',
      title: t('tutorial.slashCommands.title'),
      description: t('tutorial.slashCommands.description'),
      placement: 'top',
      action: () => {
        ensureChatOpen()
        const ta = document.querySelector('[data-slash-input]') as HTMLTextAreaElement | null
        if (ta) {
          ta.focus()
          // Use native setter so React sees the change in a controlled textarea
          try {
            const proto = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')
            proto?.set?.call(ta, '/')
          } catch {
            ta.value = '/'
          }
          ta.dispatchEvent(new Event('input', { bubbles: true }))
          try { ta.selectionStart = ta.selectionEnd = ta.value.length } catch {}

          // Blur the input after a moment so arrow keys can control window navigation
          setTimeout(() => {
            ta.blur()
          }, 500)
        }
      },
    },
  ]

  const themeSteps: TutorialStep[] = includeThemeSteps
    ? [
      {
      target: '[data-chat-input-area]',
      title: t('tutorial.generateTheme.title'),
      description: t('tutorial.generateTheme.description'),
      placement: 'top',
      action: async () => {
        console.log('[Tutorial] Theme creation step action triggered')
        ensureChatOpen()

        // If the theme already exists, do nothing special; user can click Next manually
        const alreadyExists = themeAlreadyExists()
        console.log('[Tutorial] Theme already exists?', alreadyExists)
        if (alreadyExists) {
          return
        }

        if (themeCommandSubmittedRef.current) {
          console.log('[Tutorial] Theme command already submitted, skipping')
          return
        }

        const ta = document.querySelector('[data-slash-input]') as HTMLTextAreaElement | null
        console.log('[Tutorial] Found textarea:', !!ta)
        if (!ta) return

        ta.focus()

        // Type out the /theme command character by character
        const text = t('tutorial.generateTheme.command')
        console.log('[Tutorial] Starting to type text:', text)

        // Disable Next button while typing and creating theme
        window.dispatchEvent(new Event('tutorial-disable-next'))
        console.log('[Tutorial] Disabled Next button for theme creation')

        let i = 0
        const typeChar = () => {
          if (!ta) return
          const current = text.slice(0, i)
          try {
            const proto = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')
            proto?.set?.call(ta, current)
          } catch {
            ta.value = current
          }
          ta.dispatchEvent(new Event('input', { bubbles: true }))
          try { ta.selectionStart = ta.selectionEnd = ta.value.length } catch {}

          i += 1
          if (i <= text.length) {
            setTimeout(typeChar, 40)
          } else {
            // Finished typing, now simulate submitting
            console.log('[Tutorial] Finished typing, simulating submission...')
            setTimeout(async () => {
              try {
                // Clear the input to simulate submission
                try {
                  const proto = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')
                  proto?.set?.call(ta, '')
                } catch {
                  ta.value = ''
                }
                ta.dispatchEvent(new Event('input', { bubbles: true }))

                // Enable loading state in message input
                window.dispatchEvent(new CustomEvent('tutorial-set-loading', { detail: { loading: true } }))

                // Add fake user message to chat
                const messagesContainer = document.querySelector('[data-messages-container]') as HTMLElement | null
                if (messagesContainer) {
                  console.log('[Tutorial] Adding fake user message')
                  const userMsg = document.createElement('div')
                  userMsg.className = 'tutorial-fake-message'
                  userMsg.setAttribute('data-message-role', 'user')
                  userMsg.style.cssText = `
                    margin: 8px 0;
                    padding: 10px 12px;
                    background-color: ${colors.userMsgBgAlpha};
                    border-right: 3px solid ${colors.userMsgAccent};
                    border-radius: 10px;
                    font-size: 14px;
                    line-height: 1.5;
                  `
                  userMsg.textContent = text
                  messagesContainer.appendChild(userMsg)
                  messagesContainer.scrollTop = messagesContainer.scrollHeight

                  // Find the placeholder span in the input area
                  const inputArea = document.querySelector('[data-chat-input-area]') as HTMLElement | null
                  const placeholderSpan = inputArea?.querySelector('span[key="placeholder-state"]') as HTMLElement | null
                  if (placeholderSpan) {
                    const originalText = placeholderSpan.textContent
                    placeholderSpan.textContent = 'Han is thinking...'
                    placeholderSpan.style.fontStyle = 'italic'
                    placeholderSpan.style.opacity = '0.7'
                    console.log('[Tutorial] Set placeholder to thinking')

                    // Create theme via API
                    console.log('[Tutorial] Creating theme...')
                    await new Promise(resolve => setTimeout(resolve, 1500))
                    await createTutorialTheme()
                    console.log('[Tutorial] Theme created successfully')

                    // Restore placeholder
                    placeholderSpan.textContent = originalText || 'Ask me anything...'
                    placeholderSpan.style.fontStyle = 'normal'
                    placeholderSpan.style.opacity = '1'
                    console.log('[Tutorial] Restored placeholder')
                  } else {
                    console.log('[Tutorial] Could not find placeholder, continuing...')
                    // Create theme via API anyway
                    await new Promise(resolve => setTimeout(resolve, 1500))
                    await createTutorialTheme()
                  }

                  // Add fake assistant response
                  console.log('[Tutorial] Adding fake assistant response')
                  const assistantMsg = document.createElement('div')
                  console.log('[Tutorial] Created assistantMsg element')
                  assistantMsg.className = 'tutorial-fake-message'
                  assistantMsg.setAttribute('data-message-role', 'assistant')
                  console.log('[Tutorial] Set className and data attribute')
                  assistantMsg.style.cssText = `
                    margin: 8px 0;
                    padding: 10px 12px;
                    background-color: ${colors.assistantMsgBg};
                    border-left: 3px solid ${colors.assistantMsgAccent};
                    border-radius: 10px;
                    font-size: 14px;
                    line-height: 1.5;
                  `
                  console.log('[Tutorial] Set styles on assistantMsg')
                  assistantMsg.innerHTML = t('tutorial.generateTheme.responseHtml')
                  console.log('[Tutorial] Set innerHTML on assistantMsg')
                  messagesContainer.appendChild(assistantMsg)
                  console.log('[Tutorial] Appended assistantMsg to container')
                  messagesContainer.scrollTop = messagesContainer.scrollHeight
                  console.log('[Tutorial] Scrolled to bottom')

                  // Mark these as tutorial messages for cleanup
                  console.log('[Tutorial] About to add tutorial-target class to userMsg:', !!userMsg)
                  userMsg.classList.add('tutorial-target')
                  console.log('[Tutorial] Added tutorial-target to userMsg')
                  assistantMsg.classList.add('tutorial-target')
                  console.log('[Tutorial] Added tutorial-target to assistantMsg')

                  // Add smooth opacity transition for fade-out later
                  userMsg.style.transition = 'opacity 0.5s ease-out'
                  console.log('[Tutorial] Set transition on userMsg')
                  assistantMsg.style.transition = 'opacity 0.5s ease-out'
                  console.log('[Tutorial] Set transition on assistantMsg')

                  console.log('[Tutorial] Messages added, starting 2-second wait before enabling Next')
                }

                // Wait longer before enabling Next button to give user time to read
                // and prevent accidental auto-advance
                console.log('[Tutorial] Waiting 2 seconds before enabling Next button...')
                await new Promise(resolve => setTimeout(resolve, 2000))
                console.log('[Tutorial] Wait complete, enabling next button after theme creation')

                // Disable loading state
                console.log('[Tutorial] Disabling loading state')
                window.dispatchEvent(new CustomEvent('tutorial-set-loading', { detail: { loading: false } }))

                // Wait a moment then blur the message input field and focus the Next button
                console.log('[Tutorial] Waiting 100ms before blurring textarea')
                await new Promise(resolve => setTimeout(resolve, 100))
                console.log('[Tutorial] Looking for textarea to blur')
                const textarea = document.querySelector('[data-chat-textarea]') as HTMLTextAreaElement | null
                console.log('[Tutorial] Found textarea:', !!textarea, 'Active element before blur:', document.activeElement?.tagName)
                if (textarea) {
                  textarea.blur()
                  console.log('[Tutorial] Blurred textarea, active element now:', document.activeElement?.tagName)
                }

                // Focus the Next button so arrow keys work
                console.log('[Tutorial] Waiting 50ms before focusing Next button')
                await new Promise(resolve => setTimeout(resolve, 50))
                console.log('[Tutorial] Looking for Next button')
                const nextButton = document.querySelector('[data-tutorial-next-button]') as HTMLButtonElement | null
                console.log('[Tutorial] Found Next button:', !!nextButton)
                if (nextButton) {
                  nextButton.focus({ preventScroll: true })
                  console.log('[Tutorial] Focused Next button, active element now:', document.activeElement?.tagName)
                }

                console.log('[Tutorial] Enabling Next button')
                window.dispatchEvent(new Event('tutorial-enable-next'))

              } catch (error) {
                console.error('[Tutorial] Failed to create tutorial theme:', error)
                // Still enable next even on error and disable loading
                window.dispatchEvent(new CustomEvent('tutorial-set-loading', { detail: { loading: false } }))

                // Wait a moment then blur the message input field and focus the Next button
                await new Promise(resolve => setTimeout(resolve, 100))
                const textarea = document.querySelector('[data-chat-textarea]') as HTMLTextAreaElement | null
                if (textarea) {
                  textarea.blur()
                }

                // Focus the Next button so arrow keys work
                await new Promise(resolve => setTimeout(resolve, 50))
                const nextButton = document.querySelector('[data-tutorial-next-button]') as HTMLButtonElement | null
                if (nextButton) {
                  nextButton.focus({ preventScroll: true })
                }

                window.dispatchEvent(new Event('tutorial-enable-next'))
              }
            }, 500)
          }
        }

        // Start typing
        i = 0
        typeChar()
      },
    },
    {
      target: '.tutorial-fake-message',
      title: t('tutorial.themeCreated.title'),
      description: t('tutorial.themeCreated.description'),
      placement: 'center',
      action: () => {
        ensureChatOpen()
        console.log('[Tutorial] Theme Created step - ensuring messages are visible')

        // Ensure the fake messages are visible and highlighted
        const fakeMessages = document.querySelectorAll('.tutorial-fake-message')
        console.log('[Tutorial] Found', fakeMessages.length, 'fake messages')
        fakeMessages.forEach(msg => {
          msg.classList.add('tutorial-target')
          // Ensure they're visible
          ;(msg as HTMLElement).style.opacity = '1'
        })

        // Next button should already be enabled from the previous step's setTimeout
        console.log('[Tutorial] Theme Created step ready, Next button should be enabled')
      },
    },
    ]
    : []

  const fileExplorerIntroSteps: TutorialStep[] = [
    {
      target: '[data-nav-type="fileExplorer"]',
      title: t('tutorial.fileExplorer.title'),
      description: t('tutorial.fileExplorer.description'),
      placement: 'bottom',
      action: () => {
        const el = document.querySelector('[data-nav-type="fileExplorer"]')
        el?.classList.add('tutorial-pulse')
      },
    },
    {
      target: '[data-content-type="fileExplorer"]',
      title: t('tutorial.artifactsThemesIdentities.title'),
      description: t('tutorial.artifactsThemesIdentities.description'),
      placement: 'center',
      action: async () => {
        ensureFilesOpen()
        // Wait for the window to fully render and position itself
        await new Promise(resolve => setTimeout(resolve, 500))
        // Trigger Tutorial to reposition now that window is fully rendered
        window.dispatchEvent(new Event('resize'))
      },
    },
    {
      target: '[data-folder-themes]',
      title: t('tutorial.themes.title'),
      description: t('tutorial.themes.description'),
      placement: 'right',
      action: () => {
        ensureFilesOpen()
        // Try to focus and highlight the Themes folder in the sidebar
        let attempts = 0
        const maxAttempts = 20
        const tryFocus = () => {
          attempts += 1
          const el = document.querySelector('[data-folder-themes]') as HTMLElement | null
          if (el) {
            try {
              el.classList.add('tutorial-pulse')
              el.scrollIntoView({ behavior: 'smooth', block: 'center' })
              // Click to switch currentFolder to /Themes so the table shows themes
              el.click()
            } catch {}
            return
          }
          if (attempts < maxAttempts) {
            setTimeout(tryFocus, 100)
          }
        }
        // Give the Files window a moment to render
        setTimeout(tryFocus, 50)
      },
    },
  ]

  // Only show "Apply Your Theme" step if we just created the theme
  const applyThemeStep: TutorialStep[] = includeThemeSteps
    ? [
        {
          // Try to target the honcho_theme file specifically, fallback to parent if not found
          target: '[data-file-name*="honcho_theme"], [data-content-type="fileExplorer"]',
          title: t('tutorial.applyTheme.title'),
          description: t('tutorial.applyTheme.description'),
          placement: 'center',
          action: () => {
            ensureFilesOpen()

            // First ensure the Themes folder is clicked to show themes in the file list
            const ensureThemesFolderSelected = () => {
              const themesFolder = document.querySelector('[data-folder-themes]') as HTMLElement | null
              if (themesFolder) {
                console.log('[Tutorial] Clicking Themes folder to show theme files')
                themesFolder.click()
                // Dispatch storage refresh to ensure file list updates
                window.dispatchEvent(new Event('storage-refresh'))
              }
            }

            // Click Themes folder first
            setTimeout(ensureThemesFolderSelected, 200)

            // Try to highlight the honcho_theme file if it exists
            let attempts = 0
            const maxAttempts = 30
            const tryHighlight = () => {
              attempts += 1
              const fileRow = document.querySelector('[data-file-name*="honcho_theme"]') as HTMLElement | null
              if (fileRow) {
                console.log('[Tutorial] Found honcho_theme file, highlighting it')
                fileRow.classList.add('tutorial-pulse')
                fileRow.classList.add('tutorial-target')
                fileRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
                // Also add a strong visual indicator
                fileRow.style.boxShadow = `0 0 0 3px ${colors.primaryAccent}`
                fileRow.style.animation = 'pulse 2s ease-in-out infinite'

                // Trigger tutorial tooltip reposition now that element exists
                // Tutorial component listens to resize and scroll events
                window.dispatchEvent(new Event('resize'))
                console.log('[Tutorial] Dispatched resize event to reposition tooltip')
                return
              }
              console.log(`[Tutorial] honcho_theme not found yet, attempt ${attempts}/${maxAttempts}`)
              if (attempts < maxAttempts) {
                setTimeout(tryHighlight, 200)
              } else {
                // After max attempts, still trigger resize in case we're using fallback target
                window.dispatchEvent(new Event('resize'))
                console.log('[Tutorial] Max attempts reached, using fallback target')
              }
            }
            // Give it more time to render after clicking Themes folder
            setTimeout(tryHighlight, 600)
          },
        },
      ]
    : []

  const remainingSteps: TutorialStep[] = [
    {
      target: '[data-folder-artifacts]',
      title: t('tutorial.artifacts.title'),
      description: t('tutorial.artifacts.description'),
      placement: 'right',
      action: ensureFilesOpen,
    },
    {
      target: '[data-folder-identities]',
      title: t('tutorial.identities.title'),
      description: t('tutorial.identities.description'),
      placement: 'right',
      action: ensureFilesOpen,
    },
    {
      target: '[data-nav-type="representation"]',
      title: t('tutorial.representation.title'),
      description: t('tutorial.representation.description'),
      placement: 'bottom',
      action: () => {
        const el = document.querySelector('[data-nav-type="representation"]')
        el?.classList.add('tutorial-pulse')
      },
    },
    {
      target: '[data-nav-type="settings"]',
      title: t('tutorial.settings.title'),
      description: t('tutorial.settings.description'),
      placement: 'bottom',
      action: () => {
        const el = document.querySelector('[data-nav-type="settings"]')
        el?.classList.add('tutorial-pulse')
      },
    },
    {
      target: '[data-nav-type="profile"]',
      title: t('tutorial.profile.title'),
      description: t('tutorial.profile.description'),
      placement: 'bottom',
      action: () => {
        const el = document.querySelector('[data-nav-type="profile"]')
        el?.classList.add('tutorial-pulse')
      },
    },
    {
      target: '[data-chat-input-area]',
      title: t('tutorial.marketplace.title'),
      description: t('tutorial.marketplace.description'),
      placement: 'top',
      action: () => {
        // Ensure chat is open and highlight the input area
        ensureChatOpen()
        const inputArea = document.querySelector('[data-chat-input-area]') as HTMLElement | null
        inputArea?.classList.add('tutorial-pulse')
        inputArea?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

        const ta = document.querySelector('[data-slash-input]') as HTMLTextAreaElement | null
        if (!ta) return
        ta.focus()

        const text = t('tutorial.marketplace.query')
        // If already typed once in this tutorial session, don't retype
        if (marketplaceTypedRef.current) {
          try { ta.selectionStart = ta.selectionEnd = ta.value.length } catch {}
          // Blur so arrow keys work
          setTimeout(() => ta.blur(), 500)
          return
        }

        // If the textarea already contains the full text (e.g., user navigated back), mark as typed
        if ((ta.value || '').trim() === text) {
          marketplaceTypedRef.current = true
          try { ta.selectionStart = ta.selectionEnd = ta.value.length } catch {}
          // Blur so arrow keys work
          setTimeout(() => ta.blur(), 500)
          return
        }

        let i = 0

        const typeChar = () => {
          if (!ta) return
          const current = text.slice(0, i)
          try {
            const proto = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')
            proto?.set?.call(ta, current)
          } catch {
            ta.value = current
          }
          ta.dispatchEvent(new Event('input', { bubbles: true }))
          try { ta.selectionStart = ta.selectionEnd = ta.value.length } catch {}

          i += 1
          if (i <= text.length) {
            window.setTimeout(typeChar, 40)
          } else {
            // Finished typing; flag so we don't retype again
            marketplaceTypedRef.current = true
            // Blur the input after a moment so arrow keys can control window navigation
            setTimeout(() => ta.blur(), 500)
          }
        }

        // Start typing from scratch
        i = 0
        typeChar()
      },
    },
    {
      target: '.desktop',
      title: t('tutorial.complete.title'),
      description: t('tutorial.complete.description'),
      placement: 'center',
    },
  ]

  const steps: TutorialStep[] = useMemo(
    () => [
      ...baseSteps,
      ...themeSteps,
      ...fileExplorerIntroSteps,
      ...applyThemeStep,
      ...remainingSteps,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [includeThemeSteps, t]
  )

  return {
    ...tutorial,
    steps,
    themeCommandSubmittedRef,
    marketplaceTypedRef,
  }
}

interface DesktopTutorialProps {
  onComplete?: () => void
  onSkip?: () => void
}

export default function DesktopTutorial({ onComplete, onSkip }: DesktopTutorialProps) {
  const [restartKey, setRestartKey] = useState(0)
  const { isOpen, steps, completeTutorial, skipTutorial, resetTutorial, startTutorial, themeCommandSubmittedRef, marketplaceTypedRef } = useDesktopTutorial() as ReturnType<typeof useDesktopTutorial> & { resetTutorial: () => void; startTutorial: () => void; themeCommandSubmittedRef: { current: boolean }; marketplaceTypedRef: { current: boolean } }

  // Reset refs when tutorial restarts
  useEffect(() => {
    if (restartKey > 0) {
      themeCommandSubmittedRef.current = false
      marketplaceTypedRef.current = false
    }
  }, [restartKey, themeCommandSubmittedRef, marketplaceTypedRef])

  // Expose simple controls for external triggers (e.g., TopNav menu)
  useEffect(() => {
    ;(window as any).desktopTutorial = {
      start: () => startTutorial(),
      reset: () => resetTutorial(),
      restart: () => {
        // Remove any lingering pulse classes and fake messages before restarting
        try {
          document
            .querySelectorAll('.tutorial-pulse')
            .forEach((el) => el.classList.remove('tutorial-pulse'))
          document
            .querySelectorAll('.tutorial-fake-message')
            .forEach((el) => el.remove())
        } catch {}
        // Bump key to remount Tutorial and reset internal step state
        setRestartKey((k) => k + 1)
        // Ensure state settles before reopening
        setTimeout(() => startTutorial(), 0)
      },
    }
    return () => {
      try {
        delete (window as any).desktopTutorial
      } catch {}
    }
  }, [startTutorial, resetTutorial])

  const handleComplete = () => {
    completeTutorial()
    onComplete?.()
  }
  const handleSkip = () => {
    skipTutorial()
    onSkip?.()
  }

  return (
    <Tutorial key={`desktop-tutorial-${restartKey}`} steps={steps} isOpen={isOpen} onComplete={handleComplete} onSkip={handleSkip} />
  )
}


